import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container, Paper, Typography, Box, Button, TextField,
  CircularProgress, Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import {
  applyEmailVerification, verifyResetCode, confirmReset,
} from '../firebase';

/**
 * 自前の日本語メールアクションページ。
 * Firebase のメールリンク（確認・パスワードリセット）の遷移先として使用する。
 *
 * 対応パターン:
 * 1. /auth/action?mode=verifyEmail&oobCode=...  → メール確認を実行して日本語表示
 * 2. /auth/action?mode=resetPassword&oobCode=... → 新パスワード入力フォームを表示
 * 3. クエリなし（Firebase既定ページからの continueUrl 遷移）→ 完了メッセージを表示
 */
const AuthAction = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');

  // status: 'loading' | 'verified' | 'reset-form' | 'reset-done' | 'error' | 'info'
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const run = async () => {
      // oobCode が無い場合は、Firebase 既定ページからの遷移（処理は完了済み）とみなす
      if (!oobCode) {
        setStatus('info');
        setMessage('メールの操作が完了しました。');
        return;
      }

      try {
        if (mode === 'verifyEmail') {
          await applyEmailVerification(oobCode);
          setStatus('verified');
          setMessage('メールアドレスの確認が完了しました。ログインしてご利用いただけます。');
        } else if (mode === 'resetPassword') {
          // リセットコードを検証し、対象メールを取得してフォーム表示
          const addr = await verifyResetCode(oobCode);
          setEmail(addr || '');
          setStatus('reset-form');
        } else {
          // その他のモード（recoverEmail 等）は既定処理済みとして案内
          setStatus('info');
          setMessage('メールの操作が完了しました。');
        }
      } catch (err) {
        console.error('メールアクション処理エラー:', err?.code || err?.message);
        setStatus('error');
        const code = err?.code || '';
        if (code === 'auth/expired-action-code') {
          setMessage('リンクの有効期限が切れています。お手数ですが、もう一度メールの送信をお試しください。');
        } else if (code === 'auth/invalid-action-code') {
          setMessage('このリンクは無効です。すでに使用済みか、期限が切れている可能性があります。');
        } else {
          setMessage('処理中にエラーが発生しました。時間をおいて再度お試しください。');
        }
      }
    };
    run();
  }, [mode, oobCode]);

  // パスワードリセットの送信
  const handleResetSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      setMessage('パスワードは8文字以上で入力してください。');
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage('パスワードが一致しません。');
      return;
    }
    setSubmitting(true);
    setMessage('');
    try {
      await confirmReset(oobCode, newPassword);
      setStatus('reset-done');
      setMessage('パスワードを再設定しました。新しいパスワードでログインしてください。');
    } catch (err) {
      const code = err?.code || '';
      if (code === 'auth/expired-action-code' || code === 'auth/invalid-action-code') {
        setMessage('リンクの有効期限が切れているか無効です。もう一度パスワード再設定をお試しください。');
      } else if (code === 'auth/weak-password') {
        setMessage('パスワードが脆弱です。より複雑なパスワードを設定してください。');
      } else {
        setMessage('パスワードの再設定に失敗しました。時間をおいて再度お試しください。');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderIcon = (ok) => (
    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
      {ok
        ? <CheckCircleIcon color="success" sx={{ fontSize: 64 }} />
        : <ErrorIcon color="error" sx={{ fontSize: 64 }} />}
    </Box>
  );

  return (
    <Container maxWidth="sm" sx={{ mt: 6, mb: 6 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {status === 'loading' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body1" sx={{ mt: 2 }}>処理中です...</Typography>
          </Box>
        )}

        {status === 'verified' && (
          <Box sx={{ textAlign: 'center' }}>
            {renderIcon(true)}
            <Typography variant="h6" gutterBottom>メールアドレスの確認が完了しました</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{message}</Typography>
            <Button variant="contained" color="primary" onClick={() => navigate('/login')}>
              ログインへ
            </Button>
          </Box>
        )}

        {status === 'info' && (
          <Box sx={{ textAlign: 'center' }}>
            {renderIcon(true)}
            <Typography variant="h6" gutterBottom>操作が完了しました</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{message}</Typography>
            <Button variant="contained" color="primary" onClick={() => navigate('/login')}>
              ログインへ
            </Button>
          </Box>
        )}

        {status === 'error' && (
          <Box sx={{ textAlign: 'center' }}>
            {renderIcon(false)}
            <Typography variant="h6" gutterBottom>エラー</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{message}</Typography>
            <Button variant="outlined" onClick={() => navigate('/login')}>
              ログイン画面へ戻る
            </Button>
          </Box>
        )}

        {status === 'reset-form' && (
          <Box>
            <Typography variant="h6" gutterBottom>パスワードの再設定</Typography>
            {email && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {email} の新しいパスワードを設定してください。
              </Typography>
            )}
            {message && <Alert severity="error" sx={{ mb: 2 }}>{message}</Alert>}
            <form onSubmit={handleResetSubmit}>
              <TextField
                fullWidth type="password" label="新しいパスワード（8文字以上）"
                value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                sx={{ mb: 2 }} required
              />
              <TextField
                fullWidth type="password" label="新しいパスワード（確認）"
                value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                sx={{ mb: 3 }} required
              />
              <Button type="submit" variant="contained" color="primary" fullWidth disabled={submitting}>
                {submitting ? <CircularProgress size={24} /> : 'パスワードを再設定'}
              </Button>
            </form>
          </Box>
        )}

        {status === 'reset-done' && (
          <Box sx={{ textAlign: 'center' }}>
            {renderIcon(true)}
            <Typography variant="h6" gutterBottom>パスワードを再設定しました</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{message}</Typography>
            <Button variant="contained" color="primary" onClick={() => navigate('/login')}>
              ログインへ
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default AuthAction;
